/**
 * Web chat tool-card for `skillsList`.
 * Web-only; a `.native.tsx` sibling renders the iOS/Android version.
 *
 * Rows select the skill in the current composer. The user stays in Agent Chat
 * and can add context before sending the slash command.
 *
 * What changed from the first version, and why
 * -------------------------------------------
 * It showed eight dense rows of name + a grey "Free" pill + one ellipsised line
 * of description, over a dead "15 skills" footer — while using four of the
 * thirty-plus fields `SkillSummary` already carries.
 *
 *   - The leading slot now uses `SkillCatalogIcon`, which the skills surfaces
 *     already use: country flags for the national CV skills, preset glyphs
 *     otherwise, uploaded artwork when an author supplied it. Eight rows that
 *     differ only by their text are much harder to scan than eight flags.
 *   - The tier badge is gone for free skills. Repeating "Free" on every row is
 *     eight pills of pure noise; a badge appears only where it means something.
 *   - Descriptions get two lines instead of one, so they stop truncating
 *     mid-word.
 *   - Four rows instead of eight, with a real "Show all N" action. The old
 *     footer told you there were more and gave you no way to reach them.
 */
import React, { type FC, useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useAui } from '@assistant-ui/react';
import { useTranslation } from 'react-i18next';
import type { SkillSummary } from '@tabbio/types';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { SkillCatalogIcon } from '../../skills/SkillCatalogIcon';
import { TabbioVerifiedBadge } from '../../skills/TabbioVerifiedBadge';
import { localizedCvSkillCopy } from '../../skills/cv-skill-localization';
import { formatSkillCount, formatSkillCommand } from '../../skills/skill-name';
import { runIfComposerAvailable } from '../composer-errors';
import { CardShell } from './card-shell';
import {
  CardBadge,
  CardChevron,
  CardEmpty,
  CardFooterAction,
  CardHeader,
  CardRow,
} from './card-ui';
import { asArray, safeArray, safeGet } from './types';
import type { ToolCardProps } from './types';

const MAX_DISPLAY = 4;

export const SkillListCard: FC<ToolCardProps> = ({ result }) => {
  const { t, i18n } = useTranslation('common');
  const aui = useAui();
  const router = useRouter();
  const isArabic = i18n.language?.startsWith('ar') ?? false;

  const selectSkill = useCallback(
    (skillId?: string) => {
      if (!skillId) return;
      const inserted = runIfComposerAvailable(() => {
        aui.composer().setText(`${formatSkillCommand(skillId)} `);
      });
      if (!inserted) {
        router.push(`/skills/${encodeURIComponent(skillId)}` as never);
      }
    },
    [aui, router],
  );

  const fromKey = safeArray<SkillSummary>(result, 'skills');
  const fromArray = asArray<SkillSummary>(result);
  const all = fromKey.length > 0 ? fromKey : fromArray;
  const total = safeGet<number>(result, 'total') ?? all.length;
  const skills = all.slice(0, MAX_DISPLAY);

  if (skills.length === 0) {
    return (
      <CardShell>
        <CardEmpty>{t('skills.card.listEmpty')}</CardEmpty>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <CardHeader
        title={t('skills.card.forYou')}
        leading={
          <MaterialSymbol name="bolt" size={16} style={{ color: 'var(--text-tertiary)' }} />
        }
      />

      {skills.map((skill, index) => {
        // The catalog stores both languages; an Arabic reader should not get an
        // English skill name just because the agent fetched the default field.
        const { name, description } = localizedCvSkillCopy(
          skill,
          isArabic ? 'ar' : 'en',
        );
        const isPaid = skill.tier && skill.tier !== 'free';
        const author = skill.authorName || t('skills.byTabbioName');
        const meta = `${t('skills.by', { author })} · ${t('skills.usageCount', {
          value: formatSkillCount(skill.usageCount, true),
        })}`;

        return (
          <CardRow
            key={skill.id ?? index}
            divider
            leading={<SkillCatalogIcon skill={skill} size={34} />}
            title={name}
            subtitle={description ?? undefined}
            meta={meta}
            accessibilityHint={t('skills.card.selectHint')}
            onPress={() => selectSkill(skill.skillId)}
            trailing={
              <>
                {skill.verified ? <TabbioVerifiedBadge compact /> : null}
                {isPaid ? <CardBadge>{t(`skills.tiers.${skill.tier}`)}</CardBadge> : null}
                <CardChevron />
              </>
            }
          />
        );
      })}

      {total > skills.length ? (
        <CardFooterAction
          label={t('skills.card.showAll', { count: total })}
          onPress={() => router.push('/skills' as never)}
        />
      ) : null}
    </CardShell>
  );
};
